import { useState } from "react";
import { X, Loader2, Truck, DollarSign } from "lucide-react";
import { Label } from "../ui/Label";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { shippingService } from "../../services/shippings";
import { toast } from "sonner";

interface AddShippingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (data: any) => void;
}

export function AddShippingModal({ isOpen, onClose, onSuccess }: AddShippingModalProps) {
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    price: ""
  });

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!formData.name) {
      toast.error("O nome é obrigatório");
      return;
    }

    if (formData.price === "") {
      toast.error("O valor é obrigatório (use 0 para frete grátis)");
      return;
    }

    try {
      setLoading(true);
      const response = await shippingService.create({
        name: formData.name,
        code: formData.name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-"),
        price: parseFloat(formData.price.replace(',', '.')),
        active: true
      });
      
      toast.success("Opção de frete criada com sucesso!");
      onSuccess(response.data || response);
      onClose();
      setFormData({ name: "", price: "" });
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || "Erro ao salvar frete");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[100] bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl w-full max-w-md relative border border-gray-100 shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="p-6 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#D4C070]/10 flex items-center justify-center text-[#D4C070]">
              <Truck className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Nova Opção de Frete</h3>
              <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Configure nome e custo</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold text-gray-400 uppercase">Nome do Frete</Label>
            <Input 
              value={formData.name} 
              onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
              placeholder="Ex: Sedex Internacional, Frete Fixo..." 
              className="h-11 border-gray-200 focus:ring-[#D4C070]/20" 
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-bold text-gray-400 uppercase">Valor (R$)</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input 
                type="text"
                value={formData.price} 
                onChange={(e) => setFormData({ ...formData, price: e.target.value.replace(/[^0-9,.]/g, '') })} 
                placeholder="0,00" 
                className="h-11 pl-9 border-gray-200 focus:ring-[#D4C070]/20 font-bold" 
              />
            </div>
            <p className="text-[10px] text-gray-400 italic">Digite 0 para frete grátis.</p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50/50 border-t border-gray-100 flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={loading} className="text-gray-500 font-bold uppercase text-[10px] tracking-widest">
            Cancelar
          </Button>
          <Button 
            onClick={handleSave} 
            className="bg-[#D4C070] hover:bg-[#D4C070]/90 text-white px-8 font-bold shadow-lg shadow-[#D4C070]/20 min-w-[120px]" 
            disabled={loading}
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "CRIAR FRETE"}
          </Button>
        </div>
      </div>
    </div>
  );
}
